import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Components
import RegistrationInstructions from "components/registration-form/RegistrationInstructions";
import QualificationStudentIdentifier from "components/registration-form/QualificationStudentIdentifier";
import PersonalDetails from "components/registration-form/PersonalDetails";
import LanguageCulturalDiversity from "components/registration-form/LanguageCulturalDiversity";
import Disability from "components/registration-form/Disability";
import Schooling from "components/registration-form/Schooling";
import PreviousQualifications from "components/registration-form/PreviousQualifications";
import Employment from "components/registration-form/Employment";
import StudyReason from "components/registration-form/StudyReason";
import PaymentOption from "components/registration-form/PaymentOption";
import Declaration from "components/registration-form/Declaration";
import ModalConfirmation from "components/modal/ModalConfirmation";
import AssessmentValidation from "components/registration-form/AssessmentValidation";
import AssessmentSummary from "components/registration-form/AssessmentSummary";
import Spinner from "assets/icons/general/Spinner";

// Apis
import useFetch from "utils/hooks/useFetch";
import {
  GET_COURSES_FOR_REGISTRATION,
  CREATE_CANDIDATE_REGISTRATION,
  VALIDATE_CANDIDATE_ASSESSMENT,
} from "api";

// Helper
import { registrationResultTemplate } from "utils/template/registrationResultTemplate";
import {
  generateRegistrationUUID,
  dataURLToBlob,
  formatRegistrationOptionQualification,
  getDomainITPL,
} from "utils/helpers";

function RegistrationForm() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const queryParamsEmail = params.get("email");
  const queryParamsAssessmentId = params.get("assessmentId");
  const signatureRef = useRef(null);

  const { fetch: getAllCourses, error: errorGetAllCourses } = useFetch(
    GET_COURSES_FOR_REGISTRATION,
  );
  const {
    fetch: sendCandidateRegistrationForm,
    error: errorSendCandidateRegistrationForm,
  } = useFetch(CREATE_CANDIDATE_REGISTRATION);
  const {
    fetch: validateCandidateAssessment,
    error: errorValidateCandidateAssessment,
  } = useFetch(VALIDATE_CANDIDATE_ASSESSMENT);

  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [assessmentDetail, setAssessmentDetail] = useState({});
  const [openReloadConfirmation, setOpenReloadConfirmation] = useState(false);
  const [openSuccessAssessmentValidate, setOpenSuccessAssessmentValidate] =
    useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingDone, setIsSubmittingDone] = useState(false);
  const [openFormDoneSubmitting, setOpenFormDoneSubmitting] = useState(false);

  const initialValues = {
    assessment_uuid: "",
    assessment_email_address: "",
    qualification: "",
    usi: "",
    is_australian_citizen: null,
    specified_visa_class: "",
    how_do_you_find_us: "",
    specified_how_do_you_find_us: "",
    title: "",
    other_title: "",
    single_name_only: false,
    family_name: "",
    first_name: "",
    second_name: "",
    birth_date: "",
    gender: "",
    home_phone_code: "",
    work_phone_code: "",
    mobile_phone_code: "",
    home_phone: "",
    work_phone: "",
    mobile_phone: "",
    email_address: "",
    alt_email_address: "",
    building_name: "",
    unit_details: "",
    street_number: "",
    street_name: "",
    suburb: "",
    territory: "",
    postcode: "",
    is_postal_same_as_residence: null,
    postal_building_name: "",
    postal_unit_details: "",
    postal_street_number: "",
    postal_street_name: "",
    postal_suburb: "",
    postal_territory: "",
    postal_postcode: "",
    postal_delivery_info: "",
    is_born_australian: false,
    other_birth_country: "",
    is_only_speak_english: false,
    other_speech_language: [],
    english_proficiency: "",
    cultural_origins: "",
    is_having_disability: null,
    disabilities: [],
    school_level: "",
    is_still_in_secondary_edu: null,
    is_succeed_prev_qualification: true,
    prev_qualifications: [],
    is_received_gov_fund: null,
    first_time_this_qualification: false,
    advise_qualification: "",
    employment_status: "",
    study_reason: "",
    payment_option: "",
    is_declaration_1_checked: false,
    is_declaration_2_checked: false,
    is_declaration_3_checked: false,
    is_declaration_4_checked: false,
    is_declaration_5_checked: false,
    is_declaration_6_checked: false,
    is_declaration_7_checked: false,
    is_declaration_8_checked: false,
    is_declaration_9_checked: false,
    is_privacy_notice_checked: false,
    participant_name: "",
    participant_signature: "",
  };

  const validationAssessmentSchema = Yup.object({
    assessment_uuid: Yup.string().required("Evaluation ID is required"),
    assessment_email_address: Yup.string()
      .required("Evaluation email address is required")
      .matches(
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9-]{2,6}$/,
        "Invalid email format",
      ),
  });

  const validationRegistrationSchema = Yup.object({
    qualification: Yup.string().required("Qualification is required"),
    usi: Yup.string()
      .required("USI is required")
      .length(10, "USI must be exactly 10 characters"),
    is_australian_citizen: Yup.boolean().required(
      "Identify your citizen is required",
    ),
    specified_visa_class: Yup.string().when("is_australian_citizen", {
      is: (value) => value === false,
      then: () => Yup.string().required("Specified VISA class is required"),
    }),
    how_do_you_find_us: Yup.string().required(
      "How did you find out about us is required",
    ),
    specified_how_do_you_find_us: Yup.string().when("how_do_you_find_us", {
      is: (value) =>
        value !== "" &&
        (value === "Student Referral" ||
          value === "Past Student" ||
          value === "Other"),
      then: () =>
        Yup.string().required(
          "Specified how did you find out about us is required",
        ),
    }),
    title: Yup.string().required("Title is required"),
    other_title: Yup.string().when("title", {
      is: (value) => value !== "" && value === "Other",
      then: () => Yup.string().required("Title is required"),
    }),
    family_name: Yup.string().required("Family name is required"),
    first_name: Yup.string().when("single_name_only", {
      is: (value) => value === false,
      then: () => Yup.string().required("First name class is required"),
    }),
    birth_date: Yup.string().required("Birth date is required"),
    gender: Yup.string().required("Gender is required"),
    home_phone: Yup.number().nullable(),
    home_phone_code: Yup.object().nullable(),
    work_phone: Yup.number().nullable(),
    work_phone_code: Yup.object().nullable(),
    mobile_phone: Yup.number()
      .typeError("Invalid number format")
      .required("Mobile phone number is required"),
    mobile_phone_code: Yup.object().required("Country code is required"),
    email_address: Yup.string()
      .required("Email address is required")
      .matches(
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9-]{2,6}$/,
        "Invalid email format",
      ),
    alt_email_address: Yup.string().matches(
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9-]{2,6}$/,
      "Invalid email format",
    ),
    street_number: Yup.string().required("Street number is required"),
    street_name: Yup.string().required("Street name is required"),
    suburb: Yup.string().required("Suburb is required"),
    territory: Yup.string().required("Territory is required"),
    postcode: Yup.string().required("Postcode is required"),
    is_postal_same_as_residence: Yup.boolean().required(
      "Postal address is required",
    ),
    postal_street_number: Yup.string().when("is_postal_same_as_residence", {
      is: (value) => value === false,
      then: () => Yup.string().required("Postal street number is required"),
    }),
    postal_street_name: Yup.string().when("is_postal_same_as_residence", {
      is: (value) => value === false,
      then: () => Yup.string().required("Postal street name is required"),
    }),
    postal_suburb: Yup.string().when("is_postal_same_as_residence", {
      is: (value) => value === false,
      then: () => Yup.string().required("Postal suburb is required"),
    }),
    postal_territory: Yup.string().when("is_postal_same_as_residence", {
      is: (value) => value === false,
      then: () => Yup.string().required("Postal territory is required"),
    }),
    postal_postcode: Yup.string().when("is_postal_same_as_residence", {
      is: (value) => value === false,
      then: () => Yup.string().required("Postal postcode is required"),
    }),
    postal_delivery_info: Yup.string().when("is_postal_same_as_residence", {
      is: (value) => value === false,
      then: () =>
        Yup.string().required("Postal delivery information is required"),
    }),
    is_born_australian: Yup.boolean().required("Birth country is required"),
    other_birth_country: Yup.string().when("is_born_australian", {
      is: (value) => value === false,
      then: () => Yup.string().required("Birth country is required"),
    }),
    is_only_speak_english: Yup.boolean().required(
      "Speaking language is required",
    ),
    other_speech_language: Yup.array().when("is_only_speak_english", {
      is: (value) => value === false,
      then: () =>
        Yup.array().of(Yup.object()).min(1, "Speaking language is required"),
    }),
    english_proficiency: Yup.string().when("is_only_speak_english", {
      is: (value) => value === false,
      then: () => Yup.string().required("English proficiency is required"),
    }),
    cultural_origins: Yup.string().required("Cultural origins is required"),
    is_having_disability: Yup.boolean().required(
      "Presence of having disability is required",
    ),
    disabilities: Yup.array().when("is_having_disability", {
      is: (value) => value === true,
      then: () =>
        Yup.array()
          .of(Yup.object())
          .min(1, "Presence of having disability is required"),
    }),
    school_level: Yup.string().required("School level is required"),
    is_still_in_secondary_edu: Yup.boolean().when("school_level", {
      is: (value) => value !== "" && value !== "02 – Never Attended School",
      then: () =>
        Yup.boolean().required("Still in secondary education is required"),
    }),
    prev_qualifications: Yup.array().when("is_succeed_prev_qualification", {
      is: (value) => value === true,
      then: () =>
        Yup.array()
          .of(Yup.object())
          .min(1, "Previous qualifications is required"),
    }),
    is_received_gov_fund: Yup.boolean().when("is_succeed_prev_qualification", {
      is: (value) => value === true,
      then: () =>
        Yup.boolean().required("SA Government-Based Funding is required"),
    }),
    advise_qualification: Yup.string().when("is_received_gov_fund", {
      is: (value) => value === true,
      then: () => Yup.string().required("Advise qualification is required"),
    }),
    employment_status: Yup.string().required("Employment status is required"),
    study_reason: Yup.string().required("Study reason is required"),
    payment_option: Yup.string().required("Payment option is required"),
    participant_name: Yup.string().required("Participant name is required"),
    participant_signature: Yup.mixed().required(
      "Please provide a valid signature",
    ),
    is_declaration_1_checked: Yup.boolean().oneOf(
      [true],
      "Declaration must be checked",
    ),
    is_declaration_2_checked: Yup.boolean().oneOf(
      [true],
      "Declaration must be checked",
    ),
    is_declaration_3_checked: Yup.boolean().oneOf(
      [true],
      "Declaration must be checked",
    ),
    is_declaration_4_checked: Yup.boolean().oneOf(
      [true],
      "Declaration must be checked",
    ),
    is_declaration_5_checked: Yup.boolean().oneOf(
      [true],
      "Declaration must be checked",
    ),
    is_declaration_6_checked: Yup.boolean().oneOf(
      [true],
      "Declaration must be checked",
    ),
    is_declaration_7_checked: Yup.boolean().oneOf(
      [true],
      "Declaration must be checked",
    ),
    is_declaration_8_checked: Yup.boolean().oneOf(
      [true],
      "Declaration must be checked",
    ),
    is_declaration_9_checked: Yup.boolean().oneOf(
      [true],
      "Declaration must be checked",
    ),
    is_privacy_notice_checked: Yup.boolean().oneOf(
      [true],
      "Privacy notice declaration must be checked",
    ),
  });

  const checkAtLeastWorkOrHomePhone = (values) => {
    const { home_phone_code, home_phone, work_phone_code, work_phone } = values;
    let isValid = false;
    let hasHome = false;
    let hasWork = false;

    if (
      home_phone_code &&
      home_phone_code !== "" &&
      Object.keys(home_phone_code).length > 0 &&
      home_phone &&
      home_phone !== ""
    ) {
      hasHome = true;
    }

    if (
      work_phone_code &&
      work_phone_code !== "" &&
      Object.keys(work_phone_code).length > 0 &&
      work_phone &&
      work_phone !== ""
    ) {
      hasWork = true;
    }

    if (hasHome || hasWork) {
      isValid = true;
    }

    return isValid;
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    validateField,
    validateForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues,
    ...(assessmentDetail && assessmentDetail.id
      ? {
          validationSchema: validationRegistrationSchema,
        }
      : {
          validationSchema: validationAssessmentSchema,
        }),
    onSubmit: async (values) => {
      if (assessmentDetail && assessmentDetail.id) {
        await handleSubmitCandidateRegistration(values);
      } else {
        await handleValidateCandidateAssessment();
      }
    },
  });

  useEffect(() => {
    if (assessmentDetail && assessmentDetail.id && !isSubmittingDone) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [assessmentDetail, isSubmittingDone]);

  useEffect(() => {
    if (values.school_level === "02 – Never Attended School") {
      setFieldValue(`is_still_in_secondary_edu`, false);
    }
    if (values.is_succeed_prev_qualification === false) {
      setFieldValue(`is_received_gov_fund`, false);
    }
  }, [values]);

  useEffect(() => {
    getAllCourses().then((data) => {
      if (data && data.success && data.success === true) {
        const formattedData = formatRegistrationOptionQualification(
          data.response,
        );
        setQualificationOptions(formattedData);
      }
    });
  }, [getAllCourses]);

  useEffect(() => {
    if (queryParamsAssessmentId && queryParamsAssessmentId !== "") {
      setFieldValue("assessment_uuid", queryParamsAssessmentId);
    }
    if (queryParamsEmail && queryParamsEmail !== "") {
      setFieldValue("assessment_email_address", queryParamsEmail);
    }
  }, [queryParamsEmail, queryParamsAssessmentId]);

  useEffect(() => {
    if (errorGetAllCourses && errorGetAllCourses.success === false) {
      const errMessage =
        (errorGetAllCourses && errorGetAllCourses.message) ||
        "Failed to fetch courses";
      toast.error(<p className="p-2 text-sm lg:text-base">{errMessage}</p>);
    }

    if (
      errorSendCandidateRegistrationForm &&
      errorSendCandidateRegistrationForm.success === false
    ) {
      const errMessage =
        (errorSendCandidateRegistrationForm &&
          errorSendCandidateRegistrationForm.message) ||
        "There is something wrong when submitting your registration.";
      if (errMessage === "Evaluation is already registered") {
        setIsSubmitting(false);
        setOpenFormDoneSubmitting(true);
      } else {
        setIsSubmitting(false);
        setIsSubmittingDone(true);
        toast.error(<p className="p-2 text-sm lg:text-base">{errMessage}</p>);
      }
    }

    if (
      errorValidateCandidateAssessment &&
      errorValidateCandidateAssessment.success === false
    ) {
      const errMessage =
        (errorValidateCandidateAssessment &&
          errorValidateCandidateAssessment.message) ||
        "There is something wrong when validating your evaluation. Please try again later.";
      setIsValidating(false);
      toast.error(<p className="p-2 text-sm lg:text-base">{errMessage}</p>);
    }
  }, [
    errorGetAllCourses,
    errorSendCandidateRegistrationForm,
    errorValidateCandidateAssessment,
  ]);

  const handleValidateCandidateAssessment = async () => {
    setIsValidating(true);
    const approvedAssessment = await validateCandidateAssessment({
      data: {
        assessment_uuid: values && values.assessment_uuid,
        email_address: values && values.assessment_email_address,
      },
    });

    if (
      approvedAssessment &&
      approvedAssessment.success &&
      approvedAssessment.response &&
      approvedAssessment.response.id
    ) {
      setIsValidating(false);
      setAssessmentDetail(approvedAssessment && approvedAssessment.response);
      setFieldValue(
        "qualification",
        approvedAssessment &&
          approvedAssessment.response &&
          approvedAssessment.response.selected_program,
      );
      setFieldValue(
        "participant_name",
        approvedAssessment &&
          approvedAssessment.response &&
          approvedAssessment.response.full_name,
      );
      setFieldValue(
        "email_address",
        approvedAssessment &&
          approvedAssessment.response &&
          approvedAssessment.response.email_address,
      );
      const arrayFullName =
        approvedAssessment &&
        approvedAssessment.response &&
        approvedAssessment.response.full_name &&
        approvedAssessment.response.full_name.split(" ");
      if (arrayFullName.length === 1) {
        setFieldValue(`single_name_only`, true);
        setFieldValue(`family_name`, arrayFullName[0]);
      }
      if (arrayFullName.length === 2) {
        setFieldValue(`first_name`, arrayFullName[0]);
        setFieldValue(`family_name`, arrayFullName[1]);
      }
      if (arrayFullName.length >= 3) {
        setFieldValue(`first_name`, arrayFullName[0]);
        setFieldValue(`family_name`, arrayFullName[arrayFullName.length - 1]);
        setFieldValue(
          `second_name`,
          arrayFullName.slice(1, arrayFullName.length - 1).join(" "),
        );
      }
      setOpenSuccessAssessmentValidate(true);
      setTimeout(() => {
        setOpenSuccessAssessmentValidate(false);
      }, "3000");
    }
  };

  const handleSubmitCandidateRegistration = async (values) => {
    setIsSubmitting(true);
    const inputValues = handleInputValue(values);
    // recheck user registration before submit
    const checkAssessmentRegistration = await validateCandidateAssessment({
      data: {
        assessment_uuid: values && values.assessment_uuid,
        email_address: values && values.assessment_email_address,
      },
    });

    if (
      checkAssessmentRegistration &&
      checkAssessmentRegistration.success &&
      checkAssessmentRegistration.response &&
      checkAssessmentRegistration.response.id
    ) {
      if (!checkAtLeastWorkOrHomePhone(values)) {
        setIsSubmitting(false);
        toast.error(
          <p className="p-2 text-sm lg:text-base">
            Please provide either home phone or work phone completely.
          </p>,
        );
        return;
      }
      const newRegistrationForm = await sendCandidateRegistrationForm({
        data: inputValues,
      });
      if (newRegistrationForm && newRegistrationForm.success) {
        setIsSubmitting(false);
        setIsSubmittingDone(true);
        setOpenFormDoneSubmitting(true);
      }
    }
  };

  const handleInputValue = (values) => {
    const formData = new FormData();

    const blobParticipantSignature = dataURLToBlob(
      values.participant_signature,
    );
    const registrationUUID = generateRegistrationUUID(
      assessmentDetail && assessmentDetail.selected_program,
      values.participant_signature,
    );
    const htmlRegistrationForm = registrationResultTemplate(
      values,
      assessmentDetail,
      registrationUUID,
    );
    const disabilities = values.disabilities
      .map((item) => {
        return item.value;
      })
      .join(", ");
    const other_speech_language = values.other_speech_language
      .map((item) => {
        return item.value;
      })
      .join(", ");
    const prev_qualifications = values.prev_qualifications
      .map((item) => {
        return item.value;
      })
      .join(", ");

    for (const key in values) {
      if (
        key !== "home_phone_code" &&
        key !== "work_phone_code" &&
        key !== "mobile_phone_code" &&
        key !== "participant_signature"
      ) {
        formData.append(`${key}`, values[key]);
      }
    }

    if (values.is_postal_same_as_residence) {
      formData.set("postal_building_name", values.building_name);
      formData.set("postal_unit_details", values.unit_details);
      formData.set("postal_street_number", values.street_number);
      formData.set("postal_street_name", values.street_name);
      formData.set("postal_suburb", values.suburb);
      formData.set("postal_territory", values.territory);
      formData.set("postal_postcode", values.postcode);
    }

    if (values.home_phone_code.value && values.home_phone) {
      formData.set(
        "home_phone",
        `${values.home_phone_code.value}-${values.home_phone}`,
      );
    } else {
      formData.set("home_phone", "-");
    }
    if (values.work_phone_code.value && values.work_phone) {
      formData.set(
        "work_phone",
        `${values.work_phone_code.value}-${values.work_phone}`,
      );
    } else {
      formData.set("work_phone", "-");
    }
    formData.set(
      "mobile_phone",
      `${values.mobile_phone_code.value}-${values.mobile_phone}`,
    );
    formData.set("other_speech_language", other_speech_language);
    formData.set("disabilities", disabilities);
    formData.set("prev_qualifications", prev_qualifications);

    formData.append(
      "file",
      blobParticipantSignature,
      "participant-signature-registration.png",
    );
    formData.append("registration_uuid", registrationUUID);
    formData.append("registration_date", new Date());
    formData.append("assessment_id", assessmentDetail && assessmentDetail.id);
    formData.append("template_registration_result", htmlRegistrationForm);

    return formData;
  };

  const handleClearSignature = () => {
    signatureRef && signatureRef.current && signatureRef.current.clear();
    setFieldValue("participant_signature", null);
  };

  const handleSaveSignature = () => {
    const trimmedSignatureDataURL =
      signatureRef &&
      signatureRef.current &&
      signatureRef.current.getTrimmedCanvas() &&
      signatureRef.current.getTrimmedCanvas().toDataURL("image/png");
    // make sure data URL image is not a blank / empty canvas
    if (
      trimmedSignatureDataURL &&
      trimmedSignatureDataURL.split(",")[1] !==
        "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQIW2NgAAIAAAUAAR4f7BQAAAAASUVORK5CYII="
    ) {
      setFieldValue("participant_signature", trimmedSignatureDataURL);
    }
  };

  const handleFormDoneSubmitting = () => {
    setOpenFormDoneSubmitting(false);
    setIsSubmittingDone(false);
    window.open(getDomainITPL(), "_self");
  };

  const handleBeforeUnload = (e) => {
    e.preventDefault();
    setOpenReloadConfirmation(true);
    e.returnValue = openReloadConfirmation;
    return openReloadConfirmation;
  };

  return (
    <section>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={true}
        theme="colored"
        transition={Bounce}
      />
      {assessmentDetail && assessmentDetail.id ? (
        <div className="w-full sm:max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl mx-auto space-y-8 pt-6 pb-12 px-6 lg:px-0">
          <ModalConfirmation
            open={openFormDoneSubmitting}
            setOpen={handleFormDoneSubmitting}
            title={"Thank you for the registration!"}
            description={"Your registration is successfully submitted."}
            notifyButton
          />
          <div className="space-y-2 sm:space-y-4 text-center">
            <p className="uppercase text-2xl sm:text-3xl xl:text-4xl font-bold tracking-wide">
              Registration Form
            </p>
          </div>
          {openSuccessAssessmentValidate && (
            <div className="w-3/4 sm:w-1/2 xl:w-2/5 mx-auto flex flex-row gap-2 items-center justify-center p-4 text-sm sm:text-base md:text-lg border-2 rounded-lg border-sky-500 bg-sky-100">
              <p className="text-sm sm:text-base xl:text-xl uppercase font-bold tracking-widest text-center">
                Validation Successful
              </p>
            </div>
          )}
          <RegistrationInstructions />
          <hr
            className="h-1.5 border-none text-[#333] bg-[#333]"
            style={{ marginTop: "60px" }}
          />
          <AssessmentSummary assessmentDetail={assessmentDetail} />
          <QualificationStudentIdentifier
            qualificationOptions={qualificationOptions}
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <PersonalDetails
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <LanguageCulturalDiversity
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <Disability
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <Schooling
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <PreviousQualifications
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <Employment
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <StudyReason
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <PaymentOption
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
          <Declaration
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            handleBlur={handleBlur}
            signatureRef={signatureRef}
            handleClear={handleClearSignature}
            handleSave={handleSaveSignature}
          />
          <div className="mx-12">
            <button
              className="py-3 uppercase tracking-widest bg-black text-white w-full p-2 rounded-xl text-base sm:text-xl xl:text-2xl font-semibold  tracking-widest hover:opacity-70 disabled:opacity-30"
              onClick={handleSubmit}
              disabled={isSubmitting || Object.keys(errors).length > 0}
              type="submit"
            >
              {isSubmitting && <Spinner />}
              Submit Registration
            </button>
          </div>
        </div>
      ) : (
        <div className="w-full sm:max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl mx-auto space-y-8 pt-20 pb-32 px-6 lg:px-0">
          <div className="space-y-2 sm:space-y-4 text-center">
            <p className="uppercase text-2xl sm:text-3xl xl:text-4xl font-bold tracking-wide">
              Registration Form
            </p>
          </div>
          <AssessmentValidation
            queryParamsEmail={queryParamsEmail}
            queryParamsAssessmentId={queryParamsAssessmentId}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            isValidating={isValidating}
          />
        </div>
      )}
    </section>
  );
}

export default RegistrationForm;
